/**
 * SILAE Memo Helpers — Frontend only
 *
 * Provides memo code generation and variable extraction utilities
 * used by SilaeMemoEditor.
 */

function extractVariablesFromComplexExpression(
  complexExpression: Array<{
    variableName?: string;
    sourceType?: string;
    complexExpression?: Array<unknown>;
  }>,
  generatedVars: Set<string>,
): void {
  if (!complexExpression || !Array.isArray(complexExpression)) return;
  complexExpression.forEach((item) => {
    if (item.variableName) generatedVars.add(item.variableName);
    if (
      item.sourceType === "complex_expression" &&
      item.complexExpression &&
      Array.isArray(item.complexExpression)
    ) {
      extractVariablesFromComplexExpression(
        item.complexExpression as Array<{
          variableName?: string;
          sourceType?: string;
          complexExpression?: Array<unknown>;
        }>,
        generatedVars,
      );
    }
  });
}

export function getAllGeneratedVariablesFromBonus(
  bonus: Record<string, unknown>,
): Set<string> {
  const generatedVars = new Set<string>();
  generatedVars.add("Bases");
  const method = bonus.calculMethod as string;
  if (method === "1" || method === "3") generatedVars.add("Tauxs");
  const bases = bonus.bases as Array<Record<string, unknown>> | undefined;
  if (bases && Array.isArray(bases)) {
    bases.forEach((base) => {
      const expr = base.complexExpression as Array<{ variableName?: string; sourceType?: string; complexExpression?: Array<unknown> }> | undefined;
      if (expr && Array.isArray(expr)) extractVariablesFromComplexExpression(expr, generatedVars);
    });
  }
  const tauxs = bonus.tauxs as Array<Record<string, unknown>> | undefined;
  if (tauxs && Array.isArray(tauxs)) {
    tauxs.forEach((taux) => {
      const expr = taux.complexExpression as Array<{ variableName?: string; sourceType?: string; complexExpression?: Array<unknown> }> | undefined;
      if (expr && Array.isArray(expr)) extractVariablesFromComplexExpression(expr, generatedVars);
    });
  }
  return generatedVars;
}

export function generateMemoCode(
  memoLines: string,
  generatedVariables: Set<string>,
  indent: string,
): string[] {
  const lines: string[] = [];
  if (!memoLines || !memoLines.trim()) return lines;
  const memoSplit = memoLines.split("\n");
  memoSplit.forEach((memoLine) => {
    const trimmedLine = memoLine.trim();
    if (!trimmedLine) return;
    if (trimmedLine.match(/^[-─]{10,}$/)) {
      lines.push(`${indent}Call Rem("${"-".repeat(78)}")`);
      return;
    }
    const variableRegex = /\{\{([A-Za-z_][A-Za-z0-9_.]*)\}\}/g;
    const hasVariables = variableRegex.test(trimmedLine);
    if (hasVariables) {
      const segments: Array<{ type: "text" | "variable"; content: string }> = [];
      let lastIndex = 0;
      let match;
      variableRegex.lastIndex = 0;
      while ((match = variableRegex.exec(trimmedLine)) !== null) {
        if (match.index > lastIndex) {
          const textBefore = trimmedLine.substring(lastIndex, match.index);
          if (textBefore.trim()) segments.push({ type: "text", content: textBefore });
        }
        segments.push({ type: "variable", content: match[1] });
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < trimmedLine.length) {
        const textAfter = trimmedLine.substring(lastIndex);
        if (textAfter.trim()) segments.push({ type: "text", content: textAfter });
      }
      const parts: string[] = [];
      segments.forEach((segment) => {
        if (segment.type === "text") {
          const escapedText = segment.content.replace(/"/g, '""');
          parts.push(`"${escapedText}"`);
        } else {
          const isGeneratedVariable = segment.content === "Bases" || segment.content === "Tauxs" || generatedVariables.has(segment.content);
          const looksNumeric = isGeneratedVariable || segment.content.startsWith("BUL.") || segment.content.startsWith("Bul.") || segment.content.startsWith("bul.") || segment.content.startsWith("SAL.") || segment.content.startsWith("Sal.") || segment.content.startsWith("sal.");
          if (looksNumeric) {
            parts.push(`Round(${segment.content}, 2)`);
          } else {
            parts.push(segment.content);
          }
        }
      });
      lines.push(`${indent}Call Rem(${parts.join(" + ")})`);
    } else {
      const escapedMemo = trimmedLine.replace(/"/g, '""');
      lines.push(`${indent}Call Rem("${escapedMemo}")`);
    }
  });
  return lines;
}
